import React, { useState } from "react";

import { Button, CircularProgress } from "@mui/material";

import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import {
  SolarEnergyDetail,
  SolarEnergyDetailItem,
  emptySolarEnergy,
} from "components/forms/SolarEnergyDetail";
import {
  InstallationFieldDetail,
  InstallationFieldDetailItem,
  emptyInstallationField,
} from "components/forms/InstallationFieldDetail";
import {
  SolarPanelDetails,
  SolarPanelDetailsItem,
  emptySolarPanelDetails,
} from "components/forms/SolarPanelDetails";
import {
  InverterDetails,
  InverterDetailsItem,
  emptyInverterDetails,
} from "components/forms/InverterDetails";
import { createGreenEnergy } from "api/greenEnergy";
import { useToken, useUserContext } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";
import { CustomCodes, ErrorResponse } from "contexts/ErrorContextData";

export const CreatePhotovoltaic = ({ history }) => {
  const token = useToken();
  const setError = useSetError();
  const { currentPavement } = useUserContext();

  const [solarEnergyData, setSolarEnergyData] = useState<SolarEnergyDetailItem>(
    { ...emptySolarEnergy }
  );
  const [installationData, setInstallationData] =
    useState<InstallationFieldDetailItem>({ ...emptyInstallationField });
  const [panelData, setPanelData] = useState<SolarPanelDetailsItem>({
    ...emptySolarPanelDetails,
  });
  const [inverterData, setInverterData] = useState<InverterDetailsItem>({
    ...emptyInverterDetails,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (
      isSolarEnergyDataValid() &&
      isInstallationDataValid() &&
      isPanelDataValid() &&
      isInverterDataValid()
    ) {
      const floorId = currentPavement?.id || "";
      if (!floorId) {
        setError(CustomCodes.INCOMPLETE_FORM);
        return;
      }

      setIsLoading(true);
      try {
        await createGreenEnergy(
          token,
          floorId,
          solarEnergyData,
          installationData,
          panelData,
          inverterData
        );

        history.goBack();
      } catch (error) {
        // @ts-expect-error
        setError(error.response?.data?.error as ErrorResponse);
      } finally {
        setIsLoading(false);
      }
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const handleSolarEnergyChange = (updated: SolarEnergyDetailItem) => {
    setSolarEnergyData(updated);
  };

  const handleInstallationChange = (updated: InstallationFieldDetailItem) => {
    setInstallationData(updated);
  };

  const handlePanelChange = (updated: SolarPanelDetailsItem) => {
    setPanelData(updated);
  };

  const handleInverterChange = (updated: InverterDetailsItem) => {
    setInverterData(updated);
  };

  const isSolarEnergyDataValid = (): boolean => {
    if (
      solarEnergyData.hasPhotovoltaic !== true ||
      !solarEnergyData.platformId ||
      !solarEnergyData.email ||
      !solarEnergyData.password
    ) {
      return false;
    }
    return true;
  };

  const isInstallationDataValid = (): boolean => {
    if (
      !installationData.coverage_area_installed ||
      !installationData.coverage_guidance ||
      !installationData.slope_installation_side ||
      !installationData.installation_local_condition ||
      !installationData.area_available_installation
    ) {
      return false;
    }
    return true;
  };

  const isPanelDataValid = (): boolean => {
    if (
      !panelData.fotovoltaic_panels_quantity ||
      !panelData.fotovoltaic_panels_unit_power ||
      !panelData.fotovoltaic_panels_total_power
    ) {
      return false;
    }
    return true;
  };

  const isInverterDataValid = (): boolean => {
    if (
      !inverterData.solar_inverter_quantity ||
      !inverterData.solar_inverter_unit_power ||
      !inverterData.solar_inverter_total_power
    ) {
      return false;
    }
    return true;
  };

  return (
    <PageStandard title="Energia Solar" displaySelector>
      <SolarEnergyDetail
        solarEnergyData={solarEnergyData}
        onChange={handleSolarEnergyChange}
      />

      <InstallationFieldDetail
        installationData={installationData}
        onChange={handleInstallationChange}
      />

      <SolarPanelDetails panelData={panelData} onChange={handlePanelChange} />

      <InverterDetails
        inverterData={inverterData}
        onChange={handleInverterChange}
      />

      <BlockStandard hideDivider>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            isLoading ||
            !isSolarEnergyDataValid() ||
            !isInstallationDataValid() ||
            !isPanelDataValid() ||
            !isInverterDataValid()
          }
          fullWidth
        >
          {isLoading ? <CircularProgress size={24} /> : "Salvar"}
        </Button>
      </BlockStandard>
    </PageStandard>
  );
};
